import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Typography, Table, Tag, Space, Input, Select, Button, Tooltip } from 'antd';
import { LinkOutlined, ReloadOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';
import { formatTS } from '../utils/time';
import CampaignUpdateReadModal from '../components/CampaignUpdateReadModal';

// My Hub > My Campaign Updates: everyone's own view. Active updates for my
// current team (past ones too), or that I am on the list of or included in.
// The same for every role -- the wider, Visible Teams view is Oversight >
// Projects & Tasks > Campaign Updates. Click one to read it; one waiting for
// me is acknowledged from there (after opening its link).

const MY_STATUS = {
  pending:      { label: 'To acknowledge', color: 'orange' },
  acknowledged: { label: 'Acknowledged',   color: 'green' },
  not_required: { label: 'Not required',   color: 'default' },
};

export default function MyCampaignUpdatesPage() {
  const currentUser = useContext(UserContext);
  // Campaign Updates Access (View and up, or admin): point them at the full page.
  const hasAccess = currentUser?.Role === 'admin' || (currentUser?.CanEditCampaignUpdates ?? 0) >= 1;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [fMine, setFMine] = useState(null);     // blank = not filtered
  const [fTeams, setFTeams] = useState([]);
  const [reading, setReading] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/campaign-updates/my-all')
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // ?update=<id> (the "new campaign update" alert): open that one once loaded.
  const [searchParams] = useSearchParams();
  const linked = Number(searchParams.get('update')) || null;
  const [linkedDone, setLinkedDone] = useState(false);
  useEffect(() => {
    if (!linked || linkedDone || !rows.length) return;
    const r = rows.find((x) => x.ID === linked);
    if (r) setReading(r);
    setLinkedDone(true);
  }, [linked, linkedDone, rows]);

  // Teams: only those on my own updates (e.g. an old team since moving).
  const teamOptions = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => (r.Teams || []).forEach((t) => seen.set(t.ID, t.Name)));
    return [...seen].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => !fMine || r.MyStatus === fMine)
      .filter((r) => !fTeams.length || (r.Teams || []).some((t) => fTeams.includes(t.ID)))
      .filter((r) => !q || `${r.Title} ${r.Description || ''}`.toLowerCase().includes(q));
  }, [rows, search, fMine, fTeams]);

  const today = dayjs().format('YYYY-MM-DD');

  return (
    <>
      <Typography.Title level={4} style={{ marginTop: 0 }}>My Campaign Updates</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginBottom: hasAccess ? 4 : 12 }}>
        This page only shows Active campaign updates assigned directly to you.
      </Typography.Paragraph>
      {hasAccess && (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Use the global campaign update search under Oversight to view and manage all campaign updates outside of those directly linked to you.
        </Typography.Paragraph>
      )}
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search allowClear placeholder="Search topics and descriptions" style={{ width: 260 }}
          onChange={(e) => setSearch(e.target.value)} />
        <Select allowClear placeholder="My acknowledgement" style={{ width: 200 }} value={fMine}
          onChange={(v) => setFMine(v ?? null)}
          options={Object.entries(MY_STATUS).map(([value, s]) => ({ value, label: s.label }))} />
        <Select mode="multiple" allowClear showSearch optionFilterProp="label" placeholder="Teams" style={{ minWidth: 200 }}
          maxTagCount="responsive" value={fTeams} onChange={setFTeams} options={teamOptions} />
        {/* The filters work on what is loaded; this fetches again (e.g. new updates). */}
        <Tooltip title="Refresh">
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
        </Tooltip>
      </Space>
      <Table rowKey="ID" size="small" loading={loading} dataSource={shown} scroll={{ x: 'max-content' }}
        sticky={{ offsetHeader: 0 }}
        pagination={{ defaultPageSize: 200, showSizeChanger: true, pageSizeOptions: ['50', '100', '200', '500'],
          showTotal: (t) => `${t} update${t === 1 ? '' : 's'}` }}
        onRow={(r) => ({ onClick: () => setReading(r), style: { cursor: 'pointer' } })}
        columns={[
          { title: 'Date of Update', dataIndex: 'UpdateDate', key: 'UpdateDate', width: 120,
            sorter: (a, b) => (a.UpdateDate || '').localeCompare(b.UpdateDate || ''), defaultSortOrder: 'descend' },
          { title: 'Topic', dataIndex: 'Title', key: 'Title', width: 320,
            sorter: (a, b) => (a.Title || '').localeCompare(b.Title || ''),
            render: (v, r) => (
              <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                <a>{v}</a>
                {(r.Links || []).length > 0 && <LinkOutlined style={{ marginLeft: 6, color: '#8c8c8c' }} />}
                {r.Description && (
                  <Typography.Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ fontSize: 12, margin: 0 }}>
                    {r.Description}
                  </Typography.Paragraph>
                )}
              </div>
            ) },
          { title: 'Teams', dataIndex: 'Teams', key: 'Teams', width: 140,
            render: (ts) => <Space size={4} wrap>{(ts || []).map((t) => <Tag key={t.ID}>{t.Name}</Tag>)}</Space> },
          { title: 'Ack Due', dataIndex: 'AckDueDate', key: 'AckDueDate', width: 110,
            sorter: (a, b) => (a.AckDueDate || '').localeCompare(b.AckDueDate || ''),
            render: (v, r) => (v
              ? <span style={{ color: r.MyStatus === 'pending' && v < today ? '#ff4d4f' : undefined }}>{v}</span>
              : <Typography.Text type="secondary">—</Typography.Text>) },
          { title: 'My Acknowledgement', key: 'MyStatus', width: 220,
            sorter: (a, b) => a.MyStatus.localeCompare(b.MyStatus),
            render: (_, r) => (
              <Space size={6}>
                <Tag color={MY_STATUS[r.MyStatus].color}>{MY_STATUS[r.MyStatus].label}</Tag>
                {r.MyAcknowledgedAt && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{formatTS(r.MyAcknowledgedAt)}</Typography.Text>}
              </Space>
            ) },
        ]} />

      <CampaignUpdateReadModal update={reading} canAcknowledge={reading?.MyStatus === 'pending'}
        onClose={() => setReading(null)} onAcknowledged={load} />
    </>
  );
}
